// pages/sections/NameAliasesModal.js
// Admin editor for name aliases — groups of names that mean the SAME person
// (e.g. after a name change), so a stale vendor name doesn't flag a false
// "Manager" mismatch in Client Contact. Each row is a comma-separated group.
// Reads/writes /api/diNameAliases (admin-only).

import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Modal from '../../components/Modal';
import { readError } from './reviewUtil';

export default function NameAliasesModal({ open, onClose, onSaved }) {
  const [lines, setLines] = useState(null);   // null = loading; else string[] (one group per line)
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setLines(null); setErr(null);
    fetch('/api/diNameAliases', { credentials: 'include' })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(j => { if (alive) setLines((j.groups || []).map(g => g.join(', '))); })
      .catch(e => { if (alive) { setErr(e.message); setLines([]); } });
    return () => { alive = false; };
  }, [open]);

  const setLine    = (i, val) => setLines(prev => prev.map((l, idx) => (idx === i ? val : l)));
  const addLine    = () => setLines(prev => [...(prev || []), '']);
  const removeLine = (i) => setLines(prev => prev.filter((_, idx) => idx !== i));

  async function save() {
    // Parse each row into a group; drop rows with fewer than two names.
    const groups = (lines || [])
      .map(l => l.split(',').map(s => s.trim()).filter(Boolean))
      .filter(g => g.length >= 2);
    setSaving(true);
    try {
      const res = await fetch('/api/diNameAliases', {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ groups }),
      });
      if (!res.ok) throw new Error(await readError(res));
      toast.success('Name aliases saved.');
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      toast.error(`Failed to save name aliases: ${e.message}`, { autoClose: 10000 });
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Name aliases">
      <p className="cc-alias-help">
        Each row lists names that belong to the <strong>same person</strong> (e.g. after a name
        change), separated by commas. Matching names won’t flag as a mismatch across vendors.
        Example: <code>Virginia Morris, Virginia Champagne</code>
      </p>
      {lines === null ? (
        <div className="cc-alias-loading"><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Loading…</div>
      ) : (
        <div className="cc-alias-list">
          {lines.length === 0 && <div className="cc-alias-empty">No aliases yet — add one below.</div>}
          {lines.map((l, i) => (
            <div key={i} className="cc-alias-row">
              <input className="cc-alias-input" type="text" value={l}
                placeholder="Name one, Name two"
                onChange={e => setLine(i, e.target.value)} />
              <button type="button" className="cc-alias-remove" onClick={() => removeLine(i)} title="Remove this group">×</button>
            </div>
          ))}
          <button type="button" className="cc-alias-add" onClick={addLine}>+ Add alias group</button>
        </div>
      )}
      {err && <div className="cc-alias-err">Couldn’t load aliases: {err}</div>}
      <div className="cc-alias-actions">
        <button type="button" className="cc-alias-cancel" onClick={onClose} disabled={saving}>Cancel</button>
        <button type="button" className="cc-alias-save" onClick={save} disabled={saving || lines === null}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}
